import { useRef, type DragEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

const DRAG_THRESHOLD_PX = 5;
/** Share of a step a drag must cover before it settles on the next one. */
const DRAG_ADVANCE = 0.15;

/**
 * Mouse drag-to-scroll for a scroll-snapping track: spread the returned handlers on
 * it. Touch, pen and trackpads already scroll the track natively, so only a primary
 * mouse button is handled. While dragging the track gets `.is-dragging` (styles pause
 * snapping); a drag that ends over a link or button doesn't also click it.
 * `step` measures one item of the track (a card plus its gap, a whole slide…).
 */
export function useDragScroll(step: (track: HTMLElement) => number) {
  const drag = useRef<{ id: number; x: number; left: number; moved: boolean } | null>(null);
  const swallowClick = useRef(false);

  function onPointerDown(e: ReactPointerEvent<HTMLElement>) {
    swallowClick.current = false;
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag.current = { id: e.pointerId, x: e.clientX, left: e.currentTarget.scrollLeft, moved: false };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    if (!(e.buttons & 1)) {
      drag.current = null; // released outside the track before the drag started
      return;
    }
    const dx = e.clientX - d.x;
    if (!d.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      d.moved = true;
      // Keep the drag alive outside the track; the release then lands on the
      // track instead of an item, so it doesn't count as a click on it.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.currentTarget.classList.add('is-dragging');
    }
    e.currentTarget.scrollLeft = d.left - dx;
  }

  function onPointerUp(e: ReactPointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    if (!d.moved) return;
    swallowClick.current = true;
    settle(e.currentTarget, e.currentTarget.scrollLeft - d.left, step(e.currentTarget));
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onClickCapture(e: MouseEvent<HTMLElement>) {
      // A drag that ends over an item must not also open it.
      if (!swallowClick.current) return;
      swallowClick.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    // Stop the browser's native link/image drag from hijacking the gesture.
    onDragStart(e: DragEvent<HTMLElement>) {
      e.preventDefault();
    },
  };
}

/**
 * After a mouse drag, glide to an item edge: past DRAG_ADVANCE of a step in the drag
 * direction it moves on, otherwise it falls back. Snapping stays off (.is-dragging)
 * until the glide ends, so it can't yank the track mid-way.
 */
function settle(track: HTMLElement, delta: number, step: number): void {
  const pos = track.scrollLeft / step;
  const index =
    delta > 0
      ? pos - Math.floor(pos) > DRAG_ADVANCE ? Math.ceil(pos) : Math.floor(pos)
      : Math.ceil(pos) - pos > DRAG_ADVANCE ? Math.floor(pos) : Math.ceil(pos);
  const target = Math.min(Math.max(index, 0) * step, track.scrollWidth - track.clientWidth);
  const done = () => track.classList.remove('is-dragging');
  if (Math.abs(target - track.scrollLeft) < 1) return done();
  // ponytail: Safari without `scrollend` falls back to a timer that outlasts the smooth glide.
  if ('onscrollend' in window) track.addEventListener('scrollend', done, { once: true });
  else setTimeout(done, 600);
  track.scrollTo({ left: target, behavior: 'smooth' });
}
